import { DeliveryDiscountType, OnlineOrderBranchSettingItemFragment, PickupDiscountType } from '@client/graphql/types/graphql';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import PrimaryButton from '@packages/ui/button/primary-button';
import CardContent from '@packages/ui/card/card-content';
import CardFooter from '@packages/ui/card/card-footer';
import FormLayout from '@packages/ui/form/form-layout';
import { useDialog } from '@packages/ui/modal/use-dialog';
import { useNotificationCenter } from '@packages/ui/notification/notification-center';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  DeliveryDiscountTypeToRaw,
  DeliveryDiscountValueToDisplay,
  PickupDiscountTypeToRaw,
  PickupDiscountValueToDisplay,
} from '../../online-order/setting/component/delivery-discord-util';
import DeliveryDiscountInput from '../../online-order/setting/component/delivery-discount-input';
import PickupDiscountInput from '../../online-order/setting/component/pickup-discount-input';
import useUpdateOnlineOrderConfiguration from '../../online-order/setting/logic/use-update-online-order-branch-setting';

interface OnlineOrderDiscount {
  pickupDiscountAmount: string;
  pickupDiscountType: PickupDiscountType;
  deliveryDiscountAmount: string;
  deliveryDiscountType: DeliveryDiscountType;
}

export default function OnlineOrderDiscount({ onlineOrderBranchSettingData }: {onlineOrderBranchSettingData: OnlineOrderBranchSettingItemFragment}) {
  const company = useFocusedCompanyContext();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const [update] = useUpdateOnlineOrderConfiguration();

  const { reset, getValues, setValue, watch } = useForm<OnlineOrderDiscount>({
    defaultValues: {
      pickupDiscountAmount: '',
      pickupDiscountType: PickupDiscountType.None,
      deliveryDiscountAmount: '',
      deliveryDiscountType: DeliveryDiscountType.None,
    },
  });

  useEffect(() => {
    reset({
      pickupDiscountType: onlineOrderBranchSettingData.pickupDiscountType,
      pickupDiscountAmount: PickupDiscountValueToDisplay(
        onlineOrderBranchSettingData.pickupDiscountType,
        onlineOrderBranchSettingData.pickupDiscountAmount,
        company.settings.currency,
      ).toString(),
      deliveryDiscountType: onlineOrderBranchSettingData.deliveryDiscountType,
      deliveryDiscountAmount: DeliveryDiscountValueToDisplay(
        onlineOrderBranchSettingData.deliveryDiscountType,
        onlineOrderBranchSettingData.deliveryDiscountAmount,
        company.settings.currency,
      ).toString(),
    });
  }, [company.settings.currency, onlineOrderBranchSettingData, reset]);

  const handleSubmit = () => {
    dialog.confirmAlert({
      title: t('online-order.setting-page.edit.dialog.title'),
      message: t('online-order.setting-page.edit.dialog.message'),
      textButton: t('online-order.setting-page.edit.dialog.text-button'),
      onConfirm: async () => {
        await update({
          variables: {
            branchID: onlineOrderBranchSettingData.branchID,
            input: {
              deliveryDiscountType: getValues('deliveryDiscountType'),
              deliveryDiscountAmount: DeliveryDiscountTypeToRaw(
                getValues('deliveryDiscountType'),
                getValues('deliveryDiscountAmount'),
                company.settings.currency,
              ),
              pickupDiscountType: getValues('pickupDiscountType'),
              pickupDiscountAmount: PickupDiscountTypeToRaw(
                getValues('pickupDiscountType'),
                getValues('pickupDiscountAmount'),
                company.settings.currency,
              ),
            },
          },
        });

        showAlert({
          status: 'success',
          title: t('alert.title.success'),
          message: t('online-order.setting-page.edit.alert.success.message'),
        });
      },
    });
  };


  return (
    <>
      <CardContent>
        <FormLayout>
          <DeliveryDiscountInput
            currency={company.settings.currency}
            deliveryDiscountType={watch('deliveryDiscountType')}
            onTypeChange={(type) => {
              setValue('deliveryDiscountType', type);
            }}
            onAmountChange={(amount) => {
              setValue('deliveryDiscountAmount', amount);
            }}
            deliveryDiscountAmount={watch('deliveryDiscountAmount')}
          />

          <PickupDiscountInput
            currency={company.settings.currency}
            pickupDiscountAmount={watch('pickupDiscountAmount')}
            pickupDiscountType={watch('pickupDiscountType')}
            onTypeChange={(type) => {
              setValue('pickupDiscountType', type);
            }}
            onAmountChange={(amount) => {
              setValue('pickupDiscountAmount', amount);
            }}
          />
        </FormLayout>
      </CardContent>

      <CardFooter>
        <PrimaryButton onClick={handleSubmit}>{t('common.button.submit', 'Confirm')}</PrimaryButton>
      </CardFooter>
    </>
  );
}
